"use client";

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from "react";
import { Analytics, type BeforeSendEvent as AnalyticsBeforeSendEvent } from "@vercel/analytics/next";
import { SpeedInsights } from "@vercel/speed-insights/next";
const PRODUCTION_HOSTS = new Set(["www.vortixy.net", "vortixy.net"]);

/**
 * Paths that must never be reported (auth codes, admin, billing, API, private account).
 * Matches Vercel Analytics redaction guidance (2026) + privacy-by-default for logged-in areas.
 */
const PRIVATE_PATH_PREFIXES = ["/admin", "/api/", "/auth/callback", "/auth/", "/update-password", "/reset-password", "/dashboard", "/checkout", "/banned"] as const;
const SENSITIVE_QUERY_KEYS = ["token", "code", "access_token", "refresh_token", "secret", "key", "error_description"] as const;
export function shouldEnableInsights(hostname: string): boolean {
  if (!hostname) return false;
  return PRODUCTION_HOSTS.has(hostname) || hostname.endsWith(".vercel.app");
}

/**
 * Redact secrets and drop private routes before Vercel receives the event.
 * Aligns with Vercel docs: beforeSend + redacting sensitive data (2026).
 */
export function sanitizeInsightsUrl(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl, "https://www.vortixy.net");
    const path = url.pathname;
    if (PRIVATE_PATH_PREFIXES.some(prefix => path === prefix || path.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`))) {
      return null;
    }
    for (const key of SENSITIVE_QUERY_KEYS) {
      url.searchParams.delete(key);
    }
    url.hash = "";
    return url.toString();
  } catch {
    return rawUrl;
  }
}
function analyticsBeforeSend(event: AnalyticsBeforeSendEvent): AnalyticsBeforeSendEvent | null {
  const nextUrl = sanitizeInsightsUrl(event.url);
  if (nextUrl === null) return null;
  return {
    ...event,
    url: nextUrl
  };
}
function speedInsightsBeforeSend(event: {
  url: string;
  type: "vital";
  route?: string;
}) {
  const nextUrl = sanitizeInsightsUrl(event.url);
  if (nextUrl === null) return null;
  return {
    ...event,
    url: nextUrl
  };
}
function subscribeHost(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {};
  // Hostname is stable for the session; no subscription needed beyond SSR hydrate.
  void onStoreChange;
  return () => {};
}
function getHostSnapshot(): boolean {
  return shouldEnableInsights(window.location.hostname);
}
function getHostServerSnapshot(): boolean {
  return false;
}

/**
 * Vercel Web Analytics + Speed Insights (packages v2, Resilient Intake).
 *
 * - Only on production hosts / vercel.app previews
 * - Always on for those hosts: both products are cookieless / privacy-preserving
 *   (no third-party cookies; pageviews + vitals only). Gating them behind the
 *   cookie banner made bounce traffic invisible — the main reason "analytics
 *   showed nothing" while GSC/runtime logs proved traffic.
 * - Redacts sensitive query params and private routes via beforeSend
 *
 * Marketing pixels stay gated by CookieConsent (marketing category).
 */
export function VercelInsights() {
  const $ = _c(1);
  const enabled = useSyncExternalStore(subscribeHost, getHostSnapshot, getHostServerSnapshot);
  if (!enabled) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><Analytics mode="production" beforeSend={analyticsBeforeSend} /><SpeedInsights beforeSend={speedInsightsBeforeSend} /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}